import type { JSX } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SidebarProvider } from "@/components/ui/sidebar";

// The app menu can collapse to an icon-only rail on desktop (from 1024px).
// Phones keep the drawer and tablets the full menu. The provider's choice is
// remembered per browser; storage can be unavailable (private mode), and the
// rail then falls back to the page default.
export const SIDEBAR_RAIL_STORAGE_KEY = "rosc.appSidebar.rail";
export const SIDEBAR_RAIL_BREAKPOINTS = { rail: 1024, roomy: 1280 } as const;
export const SIDEBAR_RAIL_WIDTH = "4.5rem";

export type RailChoice = "collapsed" | "expanded";
// What a page wants when nothing is stored: "expanded" everywhere, or
// "compact" (collapsed under 1280px) where the content needs the width, as on
// the visit workspace with its assistant column.
export type RailPageDefault = "expanded" | "compact";

export const readRailChoice = (): RailChoice | null => {
  try {
    const value = window.localStorage.getItem(SIDEBAR_RAIL_STORAGE_KEY);
    return value === "collapsed" || value === "expanded" ? value : null;
  } catch {
    return null;
  }
};

const writeRailChoice = (choice: RailChoice): void => {
  try {
    window.localStorage.setItem(SIDEBAR_RAIL_STORAGE_KEY, choice);
  } catch {
    // The choice then lasts for this page load only.
  }
};

export const railCollapsedFor = ({
  width,
  stored,
  pageDefault,
}: {
  width: number;
  stored: RailChoice | null;
  pageDefault: RailPageDefault;
}): boolean => {
  if (width < SIDEBAR_RAIL_BREAKPOINTS.rail) return false;
  if (stored) return stored === "collapsed";
  return pageDefault === "compact" && width < SIDEBAR_RAIL_BREAKPOINTS.roomy;
};

const currentWidth = (): number =>
  typeof window === "undefined"
    ? SIDEBAR_RAIL_BREAKPOINTS.roomy
    : window.innerWidth;

const useViewportWidth = (): number => {
  const [width, setWidth] = useState(currentWidth);
  useEffect(() => {
    const queries = Object.values(SIDEBAR_RAIL_BREAKPOINTS).map((min) =>
      window.matchMedia(`(min-width: ${min}px)`),
    );
    const update = (): void => {
      setWidth(currentWidth());
    };
    for (const query of queries) query.addEventListener("change", update);
    update();
    return (): void => {
      for (const query of queries) query.removeEventListener("change", update);
    };
  }, []);
  return width;
};

interface SidebarRail {
  // Desktop only: the toggle is shown and the rail can be used.
  canCollapse: boolean;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggle: () => void;
  setPageDefault: (value: RailPageDefault) => void;
}

const noop = (): void => {};
const SidebarRailContext = createContext<SidebarRail>({
  canCollapse: false,
  collapsed: false,
  setCollapsed: noop,
  toggle: noop,
  setPageDefault: noop,
});

export const useSidebarRail = (): SidebarRail => useContext(SidebarRailContext);

// A page asks for its default while it is mounted (only used when the
// provider has not chosen yet).
export const useSidebarRailPageDefault = (value: RailPageDefault): void => {
  const { setPageDefault } = useSidebarRail();
  useEffect(() => {
    setPageDefault(value);
    return (): void => {
      setPageDefault("expanded");
    };
  }, [setPageDefault, value]);
};

export const AppSidebarProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const width = useViewportWidth();
  const [stored, setStored] = useState(readRailChoice);
  const [pageDefault, setPageDefault] = useState<RailPageDefault>("expanded");
  const canCollapse = width >= SIDEBAR_RAIL_BREAKPOINTS.rail;
  const collapsed = railCollapsedFor({
    width,
    stored,
    pageDefault,
  });

  const setCollapsed = useCallback(
    (next: boolean): void => {
      if (!canCollapse) return;
      const choice: RailChoice = next ? "collapsed" : "expanded";
      setStored(choice);
      writeRailChoice(choice);
    },
    [canCollapse],
  );

  const value = useMemo<SidebarRail>(
    () => ({
      canCollapse,
      collapsed,
      setCollapsed,
      toggle: () => {
        setCollapsed(!collapsed);
      },
      setPageDefault,
    }),
    [canCollapse, collapsed, setCollapsed],
  );

  return (
    <SidebarRailContext.Provider value={value}>
      <SidebarProvider
        open={!collapsed}
        style={{ "--sidebar-width-icon": SIDEBAR_RAIL_WIDTH } as never}
        onOpenChange={(open) => {
          setCollapsed(!open);
        }}
      >
        {children}
      </SidebarProvider>
    </SidebarRailContext.Provider>
  );
};
